import { APPLICATION_STATUSES } from './applicationStatus.js';

/**
 * When a comedian is offered or confirmed for one event, reject their pending
 * applications for other events at the same venue in the same calendar month.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{ email: string }} offeredApp
 * @param {{ id: number, venue_id: number, event_date: string }} offeredEvent
 * @returns {Promise<{ id: number, event_id: number }[]>}
 */
export async function rejectOtherPendingApplicationsSameVenueMonth(
  supabase,
  offeredApp,
  offeredEvent
) {
  const venueId = offeredEvent.venue_id;
  const eventDate = new Date(offeredEvent.event_date);
  const monthStart = `${eventDate.getUTCFullYear()}-${String(eventDate.getUTCMonth() + 1).padStart(2, '0')}-01`;
  const nextMonth = new Date(Date.UTC(eventDate.getUTCFullYear(), eventDate.getUTCMonth() + 1, 1));
  const monthEnd = nextMonth.toISOString().split('T')[0];

  const { data: sameMonthEvents, error: evErr } = await supabase
    .from('events')
    .select('id')
    .eq('venue_id', venueId)
    .gte('event_date', monthStart)
    .lt('event_date', monthEnd);

  if (evErr) throw evErr;
  if (!sameMonthEvents || sameMonthEvents.length === 0) return [];

  const otherEventIds = sameMonthEvents.map((e) => e.id).filter((id) => id !== offeredEvent.id);
  if (otherEventIds.length === 0) return [];

  const { data: rejected, error } = await supabase
    .from('applications')
    .update({ status: APPLICATION_STATUSES.REJECTED })
    .eq('email', offeredApp.email)
    .eq('status', APPLICATION_STATUSES.PENDING)
    .in('event_id', otherEventIds)
    .select('id, event_id');

  if (error) throw error;
  return rejected || [];
}

/**
 * When an application is moved back to pending, restore previously auto-rejected
 * pending→rejected rows for the same comedian / venue-month pattern.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{ email: string }} restoredApp
 * @param {{ id: number, venue_id: number, event_date: string }} restoredEvent
 * @returns {Promise<{ id: number, event_id: number }[]>}
 */
export async function restoreRejectedApplicationsSameVenueMonth(
  supabase,
  restoredApp,
  restoredEvent
) {
  const venueId = restoredEvent.venue_id;
  const eventDate = new Date(restoredEvent.event_date);
  const monthStart = `${eventDate.getUTCFullYear()}-${String(eventDate.getUTCMonth() + 1).padStart(2, '0')}-01`;
  const nextMonth = new Date(Date.UTC(eventDate.getUTCFullYear(), eventDate.getUTCMonth() + 1, 1));
  const monthEnd = nextMonth.toISOString().split('T')[0];

  const { data: sameMonthEvents, error: evErr } = await supabase
    .from('events')
    .select('id')
    .eq('venue_id', venueId)
    .gte('event_date', monthStart)
    .lt('event_date', monthEnd);

  if (evErr) throw evErr;
  if (!sameMonthEvents || sameMonthEvents.length === 0) return [];

  const otherEventIds = sameMonthEvents.map((e) => e.id).filter((id) => id !== restoredEvent.id);
  if (otherEventIds.length === 0) return [];

  const { data: restored, error } = await supabase
    .from('applications')
    .update({ status: APPLICATION_STATUSES.PENDING })
    .eq('email', restoredApp.email)
    .eq('status', APPLICATION_STATUSES.REJECTED)
    .in('event_id', otherEventIds)
    .select('id, event_id');

  if (error) throw error;
  return restored || [];
}
